import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  FileText,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  X,
  Plus,
} from 'lucide-react';
import { useServiceRequestStats, useServiceRequests } from '../hooks/useServiceRequests';
import { serviceRequestsService } from '../services/serviceRequests';
import { sendToUser } from '../services/notifications';
import type { SendToUserRequest } from '../types/notifications';
import type {
  ServiceRequestItem,
  ServiceRequestStatus,
  ReviewServiceRequestBody,
  ServiceCategory,
  CreatedCategory,
  UserServiceStatus,
} from '../types/serviceRequests';
import { cn, formatDateTime, formatRelativeTime, formatNumber, showToast } from '../utils/helpers';
import { Badge } from '../components/ui';

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 20;

// ============================================================================
// Badge helpers
// ============================================================================

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'muted';

function statusBadge(status: ServiceRequestStatus): BadgeVariant {
  const map: Record<ServiceRequestStatus, BadgeVariant> = {
    PENDING: 'warning',
    UNDER_REVIEW: 'info',
    APPROVED: 'success',
    REJECTED: 'error',
  };
  return map[status] || 'muted';
}

function formatStatusLabel(status: ServiceRequestStatus): string {
  return status.replace(/_/g, ' ');
}

function formatServiceMode(mode: string): string {
  return mode === 'IN_PERSON' ? 'In Person' : mode === 'VIRTUAL' ? 'Virtual' : mode;
}

function formatPrice(pricePerHour: string | null, isFree: boolean): string {
  if (isFree) return 'Free';
  if (pricePerHour) return `$${pricePerHour}`;
  return '—';
}

// ============================================================================
// Stat Card
// ============================================================================

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon: Icon, color, bgColor, loading }) => (
  <div className="card p-4 overflow-hidden">
    <div className="flex items-center gap-3">
      <div className={cn('p-2 rounded-xl flex-shrink-0', bgColor)}>
        <Icon className={cn('h-5 w-5', color)} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        {loading ? (
          <div className="h-7 w-16 bg-muted/30 rounded animate-pulse mt-0.5" />
        ) : (
          <p className="text-xl font-bold text-foreground truncate">{value}</p>
        )}
      </div>
    </div>
  </div>
);

// ============================================================================
// Detail helpers
// ============================================================================

const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="pt-2 mt-2 first:mt-0 first:pt-0 overflow-hidden">
    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{title}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 min-w-0">{children}</div>
  </div>
);

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5 min-w-0">
      <span className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0">{label}</span>
      <span className="text-[10px] text-foreground text-right break-all truncate max-w-[60%]">{value}</span>
    </div>
  );
};

// ============================================================================
// Pagination
// ============================================================================

const Pagination: React.FC<{
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}> = ({ page, totalPages, total, onPageChange, loading }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
      <p className="text-xs text-muted-foreground">Page {page} of {totalPages} ({formatNumber(total)} total)</p>
      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1 || loading} className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <ChevronRight className="h-4 w-4 text-foreground rotate-180" />
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum: number;
          if (totalPages <= 5) pageNum = i + 1;
          else if (page <= 3) pageNum = i + 1;
          else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
          else pageNum = page - 2 + i;
          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              disabled={loading}
              className={cn('w-8 h-8 text-xs rounded-lg transition-colors', pageNum === page ? 'bg-primary/20 text-primary font-semibold' : 'text-muted-foreground hover:bg-primary/10')}
            >
              {pageNum}
            </button>
          );
        })}
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages || loading} className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <ChevronRight className="h-4 w-4 text-foreground" />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Create Category Modal
// ============================================================================

interface CreateCategoryModalProps {
  onCreated: (cat: CreatedCategory) => void;
  onCancel: () => void;
  submitting: boolean;
  setSubmitting: (v: boolean) => void;
}

const CreateCategoryModal: React.FC<CreateCategoryModalProps> = ({ onCreated, onCancel, submitting, setSubmitting }) => {
  const [name, setName] = useState('');
  const [ageMin, setAgeMin] = useState('0');
  const [ageMax, setAgeMax] = useState('100');

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const cat = await serviceRequestsService.createCategory({
        categoryName: name.trim(),
        ageMin: parseInt(ageMin, 10) || 0,
        ageMax: parseInt(ageMax, 10) || 100,
      });
      showToast('success', `Category "${cat.categoryName}" created`);
      onCreated(cat);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-card border border-border rounded-xl shadow-xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Create New Category</h3>
          <button onClick={onCancel} className="p-1 rounded-lg hover:bg-primary/10 transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <div>
          <label className="block text-[10px] text-muted-foreground mb-1">Category Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Music Lessons"
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] text-muted-foreground mb-1">Age Min</label>
            <input
              type="number"
              value={ageMin}
              onChange={(e) => setAgeMin(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
          <div>
            <label className="block text-[10px] text-muted-foreground mb-1">Age Max</label>
            <input
              type="number"
              value={ageMax}
              onChange={(e) => setAgeMax(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-primary/10 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !name.trim()}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-primary/20 text-primary hover:bg-primary/30 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Review Modal
// ============================================================================

interface ReviewModalProps {
  item: ServiceRequestItem;
  knownCategories: ServiceCategory[];
  onSubmit: () => void;
  onCancel: () => void;
}

const ReviewModal: React.FC<ReviewModalProps> = ({ item, knownCategories, onSubmit, onCancel }) => {
  const [action, setAction] = useState<'APPROVE' | 'REJECT'>('APPROVE');
  const [reviewNote, setReviewNote] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>(item.suggestedCategoryId ?? '');
  const [serviceName, setServiceName] = useState(item.suggestedName);
  const [ageMin, setAgeMin] = useState(String(item.ageMin));
  const [ageMax, setAgeMax] = useState(String(item.ageMax));
  const [autoCreateUserService, setAutoCreateUserService] = useState(true);
  const [userServiceStatus, setUserServiceStatus] = useState<UserServiceStatus>('ACTIVE');
  const [submitting, setSubmitting] = useState(false);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);

  // Category list — fetched from API, falls back to knownCategories
  const [categories, setCategories] = useState<ServiceCategory[]>(knownCategories);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setCategoriesLoading(true);
    serviceRequestsService.listCategories()
      .then((fetched) => {
        if (cancelled) return;
        // Merge suggestedCategory in case it's not in the list
        const ids = new Set(fetched.map((c) => c.id));
        const merged = [...fetched];
        if (item.suggestedCategory && !ids.has(item.suggestedCategory.id)) {
          merged.unshift(item.suggestedCategory);
        }
        setCategories(merged);
      })
      .catch(() => {
        if (cancelled) return;
        // Fallback to knownCategories already in state
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => { cancelled = true; };
  }, [item.suggestedCategory, knownCategories]);

  const handleCategoryCreated = (cat: CreatedCategory) => {
    const newCat: ServiceCategory = { id: cat.id, categoryName: cat.categoryName };
    setCategories((prev) => {
      if (prev.some((c) => c.id === newCat.id)) return prev;
      return [...prev, newCat];
    });
    setCategoryId(cat.id);
    setShowCreateCategory(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const body: ReviewServiceRequestBody = { action };
      if (reviewNote.trim()) body.reviewNote = reviewNote.trim();
      if (action === 'APPROVE') {
        if (categoryId) body.categoryId = Number(categoryId);
        if (serviceName.trim()) body.serviceName = serviceName.trim();
        body.ageMin = parseInt(ageMin, 10) || 0;
        body.ageMax = parseInt(ageMax, 10) || 100;
        body.autoCreateUserService = autoCreateUserService;
        if (autoCreateUserService) body.userServiceStatus = userServiceStatus;
      }
      await serviceRequestsService.review(item.id, body);

      // Notify the requester of the review outcome. Copy differs per channel,
      // so send one request per channel group; the deep link (push tap / email
      // button) opens the requester's provider profile where the offering lives.
      const offeringDeepLink = {
        screen: 'ProviderProfile',
        deepLinkParams: { providerId: item.requesterId },
      };
      const quotedName = `"${item.suggestedName}"`;
      const notifications: SendToUserRequest[] =
        action === 'APPROVE'
          ? [
              {
                userId: item.requesterId,
                channels: ['PUSH', 'IN_APP'],
                title: 'New offering approved!',
                body: `Your offering ${item.suggestedName} is now live! Let's go!`,
                metadata: offeringDeepLink,
              },
              {
                userId: item.requesterId,
                channels: ['SMS'],
                title: 'New offering approved!',
                body: `Your offering ${quotedName} is approved and live. Let's go.\n\n-Xuman.AI`,
              },
              {
                userId: item.requesterId,
                channels: ['EMAIL'],
                title: 'New offering approved!',
                body: `**New addition!**\nYour offering ${quotedName} is now live on Xuman. Let's go.`,
                metadata: { ...offeringDeepLink, emailCtaLabel: 'View my offering', emailHideTitle: true },
              },
            ]
          : [
              {
                userId: item.requesterId,
                channels: ['PUSH', 'IN_APP'],
                title: 'New offering declined',
                body: 'We encourage you to send us a new one!',
              },
              {
                userId: item.requesterId,
                channels: ['SMS'],
                title: 'New offering declined',
                body: `Your offering ${quotedName} wasn't approved. Send us a new one.\n\n-Xuman.AI`,
              },
              {
                userId: item.requesterId,
                channels: ['EMAIL'],
                title: 'New offering declined',
                body: `Your offering ${quotedName} wasn't approved. We encourage you to send us a new one.`,
                // Same profile deep link as approval — "send new offering" starts there
                metadata: { ...offeringDeepLink, emailCtaLabel: 'Submit new offering', emailHideTitle: true },
              },
            ];
      // Fire independently; a channel failure never blocks the review flow
      const notifResults = await Promise.allSettled(notifications.map((n) => sendToUser(n)));
      notifResults.forEach((result, i) => {
        if (result.status === 'rejected') {
          console.warn(
            `Failed to send review notification (${notifications[i].channels.join('/')}):`,
            result.reason,
          );
        }
      });

      showToast('success', `Service request ${action === 'APPROVE' ? 'approved' : 'rejected'}`);
      onSubmit();
    } catch (err: any) {
      // Surface what the API actually said. An AxiosError's own `message` is
      // only ever "Request failed with status code 400", which tells a reviewer
      // nothing about why — and the reasons here are all actionable: a request
      // with no delivery mode recorded, a category that no longer exists, a
      // name already taken. NestJS returns validation failures as an array of
      // strings and everything else as one, so both shapes are handled.
      const data = err?.response?.data;
      const message = Array.isArray(data?.message)
        ? data.message.join(', ')
        : data?.message || data?.error;
      showToast('error', message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onCancel}>
        <div className="bg-card border border-border rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Review Service Request</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{item.suggestedName}</p>
            </div>
            <button onClick={onCancel} className="p-1 rounded-lg hover:bg-primary/10 transition-colors">
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          {/* Action toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAction('APPROVE')}
              className={cn(
                'flex-1 py-2 text-sm font-medium rounded-lg transition-colors border',
                action === 'APPROVE'
                  ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                  : 'bg-card text-muted-foreground border-border hover:bg-primary/10'
              )}
            >
              Approve
            </button>
            <button
              onClick={() => setAction('REJECT')}
              className={cn(
                'flex-1 py-2 text-sm font-medium rounded-lg transition-colors border',
                action === 'REJECT'
                  ? 'bg-red-500/15 text-red-400 border-red-500/30'
                  : 'bg-card text-muted-foreground border-border hover:bg-primary/10'
              )}
            >
              Reject
            </button>
          </div>

          {/* Approve-specific fields */}
          {action === 'APPROVE' && (
            <div className="space-y-3">
              {/* Category selector */}
              <div>
                <label className="block text-[10px] text-muted-foreground mb-1">Category</label>
                <div className="flex items-center gap-2">
                  <select
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}
                    disabled={categoriesLoading}
                    className="flex-1 px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 disabled:opacity-50"
                  >
                    <option value="">
                      {categoriesLoading ? 'Loading categories...' : 'Select a category...'}
                    </option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.categoryName}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => setShowCreateCategory(true)}
                    className="flex items-center gap-1 px-3 py-2 text-xs font-medium bg-primary/15 text-primary border border-primary/30 rounded-lg hover:bg-primary/25 transition-colors whitespace-nowrap"
                  >
                    <Plus className="h-3 w-3" />
                    New
                  </button>
                </div>
              </div>

              {/* Service name */}
              <div>
                <label className="block text-[10px] text-muted-foreground mb-1">Service Name</label>
                <input
                  type="text"
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                />
              </div>

              {/* Age range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-muted-foreground mb-1">Age Min</label>
                  <input
                    type="number"
                    value={ageMin}
                    onChange={(e) => setAgeMin(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-muted-foreground mb-1">Age Max</label>
                  <input
                    type="number"
                    value={ageMax}
                    onChange={(e) => setAgeMax(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                  />
                </div>
              </div>

              {/* Auto-create user service */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoCreateUserService}
                    onChange={(e) => setAutoCreateUserService(e.target.checked)}
                    className="rounded border-border"
                  />
                  <span className="text-xs text-foreground">Auto-create user service listing</span>
                </label>
              </div>

              {/* User service status */}
              {autoCreateUserService && (
                <div>
                  <label className="block text-[10px] text-muted-foreground mb-1">User Service Status</label>
                  <select
                    value={userServiceStatus}
                    onChange={(e) => setUserServiceStatus(e.target.value as UserServiceStatus)}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="DRAFT">Draft</option>
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Review note */}
          <div>
            <label className="block text-[10px] text-muted-foreground mb-1">
              Review Note {action === 'REJECT' && <span className="text-muted-foreground/60">(recommended for rejections)</span>}
            </label>
            <textarea
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              placeholder="Optional note about this decision..."
              rows={3}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={onCancel}
              disabled={submitting}
              className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-primary/10 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2',
                action === 'APPROVE'
                  ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                  : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
              )}
            >
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {action === 'APPROVE' ? 'Approve' : 'Reject'}
            </button>
          </div>
        </div>
      </div>

      {/* Create Category Modal (stacked above review modal) */}
      {showCreateCategory && (
        <CreateCategoryModal
          onCreated={handleCategoryCreated}
          onCancel={() => setShowCreateCategory(false)}
          submitting={creatingCategory}
          setSubmitting={setCreatingCategory}
        />
      )}
    </>
  );
};

// ============================================================================
// Service Request Row
// ============================================================================

interface ServiceRequestRowProps {
  item: ServiceRequestItem;
  expanded: boolean;
  onToggle: () => void;
  onStartReview: () => void;
  onReview: () => void;
  detail: ServiceRequestItem | null;
  detailLoading: boolean;
}

const ServiceRequestRow: React.FC<ServiceRequestRowProps> = ({
  item,
  expanded,
  onToggle,
  onStartReview,
  onReview,
  detail,
  detailLoading,
}) => {
  const displayItem = detail || item;

  return (
    <div className={cn('card p-3 cursor-pointer transition-all overflow-hidden', expanded && 'ring-1 ring-primary/30')} onClick={onToggle}>
      {/* Collapsed row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-shrink-0">
            {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </div>
          <div className="flex-shrink-0 w-14 text-xs text-muted-foreground">
            {formatRelativeTime(item.createdAt)}
          </div>
          <div className="min-w-0 max-w-[240px]">
            <p className="text-xs text-foreground font-medium truncate">
              {item.requester.name || item.requester.email || `User #${item.requesterId}`}
            </p>
            <p className="text-[10px] text-muted-foreground truncate">{item.suggestedName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          <Badge variant={statusBadge(item.status)} size="sm">{formatStatusLabel(item.status)}</Badge>
          <span className="text-[10px] text-muted-foreground">{formatPrice(item.pricePerHour, item.isFree)}</span>
          {item.serviceModeOptions.map((mode) => (
            <Badge key={mode} variant={mode === 'VIRTUAL' ? 'info' : 'muted'} size="sm">
              {formatServiceMode(mode)}
            </Badge>
          ))}
          {/* Action buttons */}
          <div className="flex items-center gap-1 ml-1" onClick={(e) => e.stopPropagation()}>
            {item.status === 'PENDING' && (
              <button
                onClick={onStartReview}
                className="px-2.5 py-1 text-[10px] font-medium rounded-md bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 transition-colors"
              >
                Start Review
              </button>
            )}
            {item.status === 'UNDER_REVIEW' && (
              <button
                onClick={onReview}
                className="px-2.5 py-1 text-[10px] font-medium rounded-md bg-primary/15 text-primary hover:bg-primary/25 transition-colors"
              >
                Review
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
          {detailLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 text-primary animate-spin" />
            </div>
          ) : (
            <>
              {/* Request Info */}
              <DetailSection title="Request Info">
                <DetailRow label="Service Name" value={displayItem.suggestedName} />
                <DetailRow label="Description" value={displayItem.description || '—'} />
                <DetailRow label="Suggested Category" value={displayItem.suggestedCategory?.categoryName || '—'} />
                <DetailRow label="Price" value={formatPrice(displayItem.pricePerHour, displayItem.isFree)} />
                <DetailRow label="Duration" value={`${displayItem.duration} min`} />
                <DetailRow label="Age Range" value={`${displayItem.ageMin}–${displayItem.ageMax}`} />
                <DetailRow label="Max Combo Size" value={String(displayItem.maxComboSize)} />
                <DetailRow label="Service Modes" value={displayItem.serviceModeOptions.map(formatServiceMode).join(', ')} />
              </DetailSection>

              {/* Requester */}
              <DetailSection title="Requester">
                <DetailRow label="Name" value={displayItem.requester.name || '—'} />
                <DetailRow label="Email" value={displayItem.requester.email || '—'} />
                <DetailRow label="Phone" value={displayItem.requester.phoneNumber || undefined} />
                <DetailRow label="User ID" value={String(displayItem.requesterId)} />
              </DetailSection>

              {/* Review info (if reviewed) */}
              {(displayItem.status === 'APPROVED' || displayItem.status === 'REJECTED') && (
                <DetailSection title="Review">
                  <DetailRow label="Decision" value={
                    <Badge variant={statusBadge(displayItem.status)} size="sm">{formatStatusLabel(displayItem.status)}</Badge>
                  } />
                  <DetailRow label="Review Note" value={displayItem.reviewNote || '—'} />
                  <DetailRow label="Reviewed At" value={displayItem.reviewedAt ? formatDateTime(displayItem.reviewedAt) : '—'} />
                  <DetailRow label="Reviewed By" value={displayItem.reviewedById ? `Admin #${displayItem.reviewedById}` : '—'} />
                  {displayItem.createdService && (
                    <>
                      <DetailRow label="Created Service" value={displayItem.createdService.serviceName} />
                      <DetailRow label="Assigned Category" value={displayItem.createdService.serviceCategory.categoryName} />
                    </>
                  )}
                  {displayItem.createdUserService && (
                    <>
                      <DetailRow label="User Service" value={displayItem.createdUserService.serviceName} />
                      <DetailRow label="User Service Status" value={displayItem.createdUserService.status} />
                    </>
                  )}
                </DetailSection>
              )}

              {/* Timeline */}
              <DetailSection title="Timeline">
                <DetailRow label="Created" value={formatDateTime(displayItem.createdAt)} />
                <DetailRow label="Updated" value={formatDateTime(displayItem.updatedAt)} />
                <DetailRow label="Reviewed" value={displayItem.reviewedAt ? formatDateTime(displayItem.reviewedAt) : undefined} />
              </DetailSection>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Page
// ============================================================================

const ServiceRequests: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<ServiceRequestStatus | ''>('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<{ id: number; data: ServiceRequestItem | null; loading: boolean } | null>(null);
  const [reviewModal, setReviewModal] = useState<ServiceRequestItem | null>(null);
  const [startingReview, setStartingReview] = useState<number | null>(null);

  const listParams = useMemo(() => ({
    status: statusFilter || undefined,
    skip: (page - 1) * PAGE_SIZE,
    take: PAGE_SIZE,
  }), [statusFilter, page]);

  const { data: stats, loading: statsLoading, refetch: refetchStats } = useServiceRequestStats();
  const { items, total, loading, error, page: currentPage, totalPages, refetch: refetchList } = useServiceRequests(listParams);

  // Collect unique categories from all loaded items (for the review modal dropdown)
  const knownCategories = useMemo<ServiceCategory[]>(() => {
    const map = new Map<number, ServiceCategory>();
    for (const item of items) {
      if (item.suggestedCategory) {
        map.set(item.suggestedCategory.id, item.suggestedCategory);
      }
      if (item.createdService?.serviceCategory) {
        map.set(item.createdService.serviceCategory.id, item.createdService.serviceCategory);
      }
    }
    return Array.from(map.values());
  }, [items]);

  const refetchAll = useCallback(async () => {
    await Promise.all([refetchStats(), refetchList()]);
  }, [refetchStats, refetchList]);

  const handleToggleExpand = useCallback(async (item: ServiceRequestItem) => {
    if (expandedId === item.id) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(item.id);
    setExpandedDetail({ id: item.id, data: null, loading: true });
    try {
      const data = await serviceRequestsService.getDetail(item.id);
      setExpandedDetail({ id: item.id, data, loading: false });
    } catch {
      setExpandedDetail({ id: item.id, data: null, loading: false });
    }
  }, [expandedId]);

  const handleStartReview = useCallback(async (item: ServiceRequestItem) => {
    setStartingReview(item.id);
    try {
      await serviceRequestsService.startReview(item.id);
      showToast('success', `Started review for "${item.suggestedName}"`);
      refetchAll();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to start review');
    } finally {
      setStartingReview(null);
    }
  }, [refetchAll]);

  const handleReviewSubmit = useCallback(() => {
    setReviewModal(null);
    setExpandedId(null);
    setExpandedDetail(null);
    refetchAll();
  }, [refetchAll]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4 lg:p-6">
        <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground">Service Requests</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Review and manage custom service requests from users</p>
          </div>
          <button
            onClick={() => refetchAll()}
            disabled={loading || statsLoading}
            className="p-2 rounded-lg hover:bg-primary/10 transition-colors"
          >
            <RefreshCw className={cn('h-4 w-4 text-muted-foreground', (loading || statsLoading) && 'animate-spin')} />
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <StatCard label="Pending" value={stats ? formatNumber(stats.pending) : '—'} icon={Clock} color="text-yellow-400" bgColor="bg-yellow-400/10" loading={statsLoading} />
          <StatCard label="Under Review" value={stats ? formatNumber(stats.underReview) : '—'} icon={Eye} color="text-blue-400" bgColor="bg-blue-400/10" loading={statsLoading} />
          <StatCard label="Approved" value={stats ? formatNumber(stats.approved) : '—'} icon={CheckCircle} color="text-emerald-400" bgColor="bg-emerald-400/10" loading={statsLoading} />
          <StatCard label="Rejected" value={stats ? formatNumber(stats.rejected) : '—'} icon={XCircle} color="text-red-400" bgColor="bg-red-400/10" loading={statsLoading} />
          <StatCard label="Total" value={stats ? formatNumber(stats.total) : '—'} icon={FileText} color="text-purple-400" bgColor="bg-purple-400/10" loading={statsLoading} />
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as ServiceRequestStatus | ''); setPage(1); }}
            className="px-3 py-2 text-xs bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          >
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="UNDER_REVIEW">Under Review</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
          <span className="text-xs text-muted-foreground">{formatNumber(total)} requests</span>
        </div>

        {/* Error state */}
        {error && (
          <div className="card p-4 border-red-500/30">
            <div className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-4 w-4" />
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* List */}
        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 text-primary animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="card p-12 text-center">
            <FileText className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No service requests found</p>
            <p className="text-xs text-muted-foreground/60 mt-1">
              {statusFilter ? 'Try changing the status filter' : 'No requests have been submitted yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <ServiceRequestRow
                key={item.id}
                item={item}
                expanded={expandedId === item.id}
                onToggle={() => handleToggleExpand(item)}
                onStartReview={() => handleStartReview(item)}
                onReview={() => setReviewModal(item)}
                detail={expandedDetail?.id === item.id ? expandedDetail.data : null}
                detailLoading={expandedDetail?.id === item.id ? expandedDetail.loading : false}
              />
            ))}
          </div>
        )}

        <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
        </div>
      </div>

      {/* Review Modal */}
      {reviewModal && (
        <ReviewModal
          item={reviewModal}
          knownCategories={knownCategories}
          onSubmit={handleReviewSubmit}
          onCancel={() => setReviewModal(null)}
        />
      )}

      {/* Loading overlay for start review */}
      {startingReview !== null && (
        <div className="fixed inset-0 bg-black/20 z-40 flex items-center justify-center">
          <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 shadow-xl">
            <Loader2 className="h-5 w-5 text-primary animate-spin" />
            <span className="text-sm text-foreground">Starting review...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceRequests;
